import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { newsList } from "@/mocks/news";

const shareTargets = [
  { icon: "ri-weibo-fill", label: "微博" },
  { icon: "ri-bilibili-fill", label: "哔哩哔哩" },
  { icon: "ri-wechat-fill", label: "微信" },
  { icon: "ri-link", label: "复制链接" },
];

export default function NewsDetailPage() {
  const { id } = useParams();
  const news = useMemo(() => newsList.find((n) => n.id === id), [id]);
  const [toast, setToast] = useState<string | null>(null);

  useDocumentMeta(
    news ? `${news.title} - h动漫` : "资讯不存在 - h动漫",
    news ? news.summary : "该资讯不存在或已删除。"
  );

  const related = useMemo(() => {
    if (!news) return [];
    return newsList
      .filter((n) => n.id !== news.id && n.tags.some((t) => news.tags.includes(t)))
      .concat(newsList.filter((n) => n.id !== news.id && n.category === news.category))
      .filter((n, idx, arr) => arr.findIndex((x) => x.id === n.id) === idx)
      .slice(0, 3);
  }, [news]);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 1800);
  };

  if (!news) {
    return (
      <div className="w-full px-4 md:px-6 py-10">
        <EmptyState
          icon="ri-error-warning-line"
          title="该资讯不存在或已删除"
          description="你访问的资讯可能已经被移除，去看看其他最新资讯吧。"
          actionLabel="返回资讯列表"
          onAction={() => {
            window.REACT_APP_NAVIGATE?.("/news");
          }}
        />
      </div>
    );
  }

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-8">
      <article className="max-w-3xl mx-auto">
        <Breadcrumb
          items={[{ label: "首页", to: "/" }, { label: "动漫资讯", to: "/news" }, { label: news.categoryLabel }]}
        />

        <header className="mt-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to="/news"
              className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium hover:bg-accent-200 transition-colors cursor-pointer"
            >
              {news.categoryLabel}
            </Link>
            {news.tags.map((t) => (
              <span key={t} className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs">
                #{t}
              </span>
            ))}
          </div>
          <h1 className="mt-3 font-heading font-extrabold text-foreground-950 text-2xl md:text-3xl leading-snug">
            {news.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-xs md:text-sm text-foreground-600 border-b border-background-200/70 pb-4">
            <span className="inline-flex items-center gap-1">
              <i className="ri-user-3-line" /> {news.author}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-newspaper-line" /> {news.source}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-time-line" /> {news.publishedAt}
            </span>
          </div>
        </header>

        <figure className="mt-6 rounded-lg overflow-hidden bg-background-200">
          <img
            src={news.cover}
            alt={news.title}
            title={`${news.title} - 动漫资讯`}
            className="w-full h-auto object-cover object-top"
          />
        </figure>

        <div className="mt-6 space-y-4">
          <p className="text-base md:text-lg text-foreground-800 leading-loose font-medium">{news.summary}</p>
          {news.content.map((para, idx) => (
            <p key={idx} className="text-sm md:text-base text-foreground-700 leading-loose">
              {para}
            </p>
          ))}
        </div>

        <section className="mt-8 rounded-lg border border-background-200/70 bg-background-50 p-4 md:p-5">
          <h2 className="flex items-center gap-2 font-heading font-semibold text-foreground-950 text-sm">
            <i className="ri-share-forward-line text-primary-500" />
            分享这篇资讯
          </h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {shareTargets.map((s) => (
              <button
                key={s.label}
                type="button"
                onClick={() => showToast(`已唤起「${s.label}」分享（演示）`)}
                className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-background-100 text-foreground-700 text-sm hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className={s.icon} />
                {s.label}
              </button>
            ))}
          </div>
        </section>

        <nav className="mt-6 flex flex-wrap gap-2">
          <Link
            to="/news"
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-background-100 text-foreground-700 text-sm hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-s-line" /> 返回资讯列表
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-background-100 text-foreground-700 text-sm hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line" /> 返回首页
          </Link>
        </nav>

        {related.length > 0 && (
          <section className="mt-10">
            <h2 className="font-heading font-bold text-foreground-950 text-lg mb-4">相关推荐资讯</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
              {related.map((n) => (
                <Link
                  key={n.id}
                  to={`/news/${n.id}`}
                  className="group rounded-lg overflow-hidden border border-background-200/70 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <div className="w-full aspect-[16/10] overflow-hidden bg-background-200">
                    <img
                      src={n.cover}
                      alt={n.title}
                      title={n.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-3">
                    <p className="text-xs text-accent-700 mb-1">{n.categoryLabel}</p>
                    <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                      {n.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-md bg-foreground-950 text-background-50 text-sm shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}